import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adm, apiErr } from "../../lib/adminApi";
import { PageHead, TInput, TArea, Btn, Card, MediaPicker, Toggle, ApprovalSel } from "../../components/admin/ui";

export default function AdminHomepage() {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { adm.homepage().then((d) => setDraft(d.draft || d.published)).catch(() => {}); }, []);
  if (!draft) return <p className="text-white/40 font-mono text-xs">LOADING…</p>;

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setCa = (f, v) => setDraft((d) => ({ ...d, copyApproval: { ...(d.copyApproval || {}), [f]: v } }));
  const Appr = ({ f, testid }) => (
    <div className="flex justify-end -mb-3"><ApprovalSel value={draft.copyApproval?.[f]} onChange={(v) => setCa(f, v)} testid={testid} /></div>
  );
  const setCta = (k, f, v) => setDraft((d) => ({ ...d, [k]: { ...(d[k] || {}), [f]: v } }));

  const save = async () => {
    setSaving(true);
    try { await adm.saveHomepageDraft(draft); toast.success("Draft saved."); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const publish = async () => {
    if (!window.confirm("Publish the current draft to the live homepage?")) return;
    try { await adm.saveHomepageDraft(draft); await adm.publishHomepage(); toast.success("Homepage published."); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const preview = async () => {
    try { await adm.saveHomepageDraft(draft); window.open("/?preview=1", "_blank"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid="admin-homepage">
      <PageHead title="Homepage" sub="Draft → Preview → Publish. Changes go live only when you publish.">
        <Btn variant="ghost" onClick={save} disabled={saving} testid="homepage-save-draft">SAVE DRAFT</Btn>
        <Btn variant="ghost" onClick={preview} testid="homepage-preview">PREVIEW</Btn>
        <Btn onClick={publish} testid="homepage-publish">PUBLISH</Btn>
      </PageHead>

      <Card className="space-y-5">
        <p className="font-mono text-[10px] tracking-[0.3em] text-gold">HERO</p>
        <div className="grid md:grid-cols-2 gap-5">
          <MediaPicker label="DESKTOP HERO IMAGE" value={draft.heroImageDesktop} onChange={(v) => set("heroImageDesktop", v)} mode="url" filterType="image" testid="hp-hero-desktop" />
          <MediaPicker label="MOBILE HERO IMAGE" value={draft.heroImageMobile} onChange={(v) => set("heroImageMobile", v)} mode="url" filterType="image" testid="hp-hero-mobile" />
          <TInput label="HERO VIDEO URL (OPTIONAL)" value={draft.heroVideoUrl} onChange={(v) => set("heroVideoUrl", v)} testid="hp-hero-video" />
          <TInput label="EYEBROW" value={draft.eyebrow} onChange={(v) => set("eyebrow", v)} testid="hp-eyebrow" />
          <TInput label="HEADLINE LINE 1" value={draft.headlineLines?.[0]} onChange={(v) => set("headlineLines", [v, draft.headlineLines?.[1] || ""])} testid="hp-headline-1" />
          <TInput label="HEADLINE LINE 2" value={draft.headlineLines?.[1]} onChange={(v) => set("headlineLines", [draft.headlineLines?.[0] || "", v])} testid="hp-headline-2" />
          <div className="md:col-span-2">
            <Appr f="supportingCopy" testid="hp-approval-supporting" />
            <TInput label="SUPPORTING COPY" value={draft.supportingCopy} onChange={(v) => set("supportingCopy", v)} testid="hp-supporting" />
          </div>
          <TInput label="PRIMARY CTA LABEL" value={draft.primaryCta?.label} onChange={(v) => setCta("primaryCta", "label", v)} testid="hp-cta1-label" />
          <TInput label="PRIMARY CTA TARGET" value={draft.primaryCta?.target} onChange={(v) => setCta("primaryCta", "target", v)} testid="hp-cta1-target" />
          <TInput label="SECONDARY CTA LABEL" value={draft.secondaryCta?.label} onChange={(v) => setCta("secondaryCta", "label", v)} testid="hp-cta2-label" />
          <TInput label="SECONDARY CTA TARGET" value={draft.secondaryCta?.target} onChange={(v) => setCta("secondaryCta", "target", v)} testid="hp-cta2-target" />
        </div>
      </Card>

      <Card className="space-y-5 mt-6">
        <p className="font-mono text-[10px] tracking-[0.3em] text-gold">FEATURED CONTENT</p>
        <div className="grid md:grid-cols-2 gap-5">
          <TInput label="FEATURED RELEASE SLUG" value={draft.featuredReleaseSlug} onChange={(v) => set("featuredReleaseSlug", v)} testid="hp-featured-release" />
          <TInput label="FEATURED TRACK (OPTIONAL)" value={draft.featuredTrack} onChange={(v) => set("featuredTrack", v)} testid="hp-featured-track" />
          <TInput label="FEATURED VISUAL ID" value={draft.featuredVisualId} onChange={(v) => set("featuredVisualId", v)} testid="hp-featured-visual" />
          <TInput label="STORE PREVIEW LOOKUP KEYS (COMMA-SEPARATED, EMPTY = AUTO)" value={(draft.storePreviewKeys || []).join(",")}
            onChange={(v) => set("storePreviewKeys", v.split(",").map((s) => s.trim()).filter(Boolean))} testid="hp-store-keys" />
        </div>
      </Card>

      <Card className="space-y-5 mt-6">
        <p className="font-mono text-[10px] tracking-[0.3em] text-gold">SECTIONS &amp; SIGNALS</p>
        <div className="grid md:grid-cols-2 gap-5">
          <TInput label="FEATURED MUSIC HEADING" value={draft.nowInMotionHeading} onChange={(v) => set("nowInMotionHeading", v)} testid="hp-nim-heading" />
          <TInput label="NEWSLETTER HEADING" value={draft.newsletterHeading} onChange={(v) => set("newsletterHeading", v)} testid="hp-newsletter-heading" />
          <div>
            <Appr f="newsletterCopy" testid="hp-approval-newsletter" />
            <TArea label="NEWSLETTER COPY" value={draft.newsletterCopy} onChange={(v) => set("newsletterCopy", v)} rows={2} testid="hp-newsletter-copy" />
          </div>
          <TInput label="NEWSLETTER BUTTON LABEL" value={draft.newsletterButtonLabel} onChange={(v) => set("newsletterButtonLabel", v)} testid="hp-newsletter-button" />
        </div>
        <div className="flex flex-wrap gap-8 pt-2">
          {[["supporters", "SUPPORTERS WALL"], ["completeSet", "COMPLETE SET"], ["dimensionV", "DIMENSION V TEASER"], ["newsletter", "NEWSLETTER"]].map(([k, l]) => (
            <Toggle key={k} label={l} checked={(draft.sectionsEnabled || {})[k] !== false}
              onChange={(v) => set("sectionsEnabled", { ...(draft.sectionsEnabled || {}), [k]: v })}
              testid={`hp-section-${k}`} />
          ))}
        </div>
      </Card>

      <Card className="space-y-5 mt-6">
        <p className="font-mono text-[10px] tracking-[0.3em] text-gold">SOCIAL PREVIEW CARD (SHARE LINK: /api/share/home)</p>
        <div className="grid md:grid-cols-3 gap-5">
          <TInput label="SOCIAL TITLE" value={draft.social?.title} onChange={(v) => set("social", { ...(draft.social || {}), title: v || null })} testid="hp-social-title" />
          <TInput label="SOCIAL DESCRIPTION" value={draft.social?.description} onChange={(v) => set("social", { ...(draft.social || {}), description: v || null })} testid="hp-social-desc" />
          <MediaPicker label="SOCIAL ARTWORK" value={draft.social?.image} onChange={(v) => set("social", { ...(draft.social || {}), image: v || null })} mode="url" filterType="image" testid="hp-social-image" />
        </div>
      </Card>

      <Card className="space-y-5 mt-6">
        <p className="font-mono text-[10px] tracking-[0.3em] text-gold">STATEMENTS</p>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <Appr f="artistHeading" testid="hp-approval-artist-heading" />
            <TInput label="ARTIST HEADING" value={draft.artistHeading} onChange={(v) => set("artistHeading", v)} testid="hp-artist-heading" />
          </div>
          <div>
            <Appr f="artistCopy" testid="hp-approval-artist-copy" />
            <TArea label="ARTIST COPY" value={draft.artistCopy} onChange={(v) => set("artistCopy", v)} rows={3} testid="hp-artist-copy" />
          </div>
          <div>
            <Appr f="finalStatement" testid="hp-approval-final" />
            <TInput label="FINAL STATEMENT" value={draft.finalStatement} onChange={(v) => set("finalStatement", v)} testid="hp-final-statement" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TInput label="FINAL CTA LABEL" value={draft.finalCta?.label} onChange={(v) => setCta("finalCta", "label", v)} testid="hp-final-cta-label" />
            <TInput label="FINAL CTA TARGET" value={draft.finalCta?.target} onChange={(v) => setCta("finalCta", "target", v)} testid="hp-final-cta-target" />
          </div>
        </div>
      </Card>
    </div>
  );
}
